import { getPageNumber, getQueries, mapTargetBySelectors } from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import qs from 'query-string';

enum Selectors {
  // Target
  Pagination = '.gtm-pagination-container a:not(.Mui-disabled)',
  Result = '.gtm-result',
  // Containers
  PaginationContainer = '.gtm-pagination-container',
}

export const mapTarget = mapTargetBySelectors([Selectors.Pagination, Selectors.Result]);

export const onDatalayer = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;
  const isResult = target?.classList.contains(Selectors.Result.replace('.', ''));
  const isPagination = !!target?.closest(Selectors.PaginationContainer);

  const {
    q: searchQuery = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keywords = '',
    page = 1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'most-relevant',
    size,
    ...filters
  } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  if (isPagination) {
    const pageNumber = getPageNumber({ target, page: Number(page) });

    return {
      event: 'clickPagination',
      eventCategory: 'Marketplace Search',
      pageNumber,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isResult) {
    const href = target?.querySelector('a')?.href || '';

    return {
      event: 'clickResult',
      eventCategory: 'Marketplace Search',
      searchFilters,
      searchQuery,
      href,
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

export const onDatalayerCustomSearch = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    q: searchQuery = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keywords = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    page = 1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'most-relevant',
    size,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...filters
  } = qs.parse(window.location.search);

  const { filters: targetFilters = {}, variantName = '', ...targetArgs } = target || {};
  const filtersArgs = { ...filters, ...targetFilters };
  const searchFilters = getQueries(filtersArgs);

  return {
    event: 'searchQuery',
    eventCategory: 'Marketplace Search',
    ...targetArgs,
    searchFilters,
    ...(variantName && { variant_name: variantName }),
  };
};

export const onDatalayerCustomSort = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const {
    params: { sortOption, variantName = '' },
  } = target || {};

  const {
    q: searchQuery = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keywords = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    page = 1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'most-relevant',
    size,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...filters
  } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  return {
    event: 'sortResults',
    eventCategory: 'Marketplace Search',
    sortOption,
    searchFilters,
    searchQuery,
    ...(variantName && { variant_name: variantName }),
  };
};

export const onDatalayerCustomFilters = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const {
    params: { filters, variantName = '' },
  } = target || {};

  const { searchBy } = qs.parse(window.location.search);

  const { q: searchQuery = '' } = qs.parse(window.location.search);

  const searchFilters = getQueries({ ...filters, searchBy });

  return {
    event: 'clickFilters',
    eventCategory: 'Marketplace Search',
    searchFilters,
    searchQuery,
    ...(variantName && { variant_name: variantName }),
  };
};
